import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/images/logo.svg';

import { API_BASE_URL } from '../config';

const Community = () => {
  const [activeTab, setActiveTab] = useState('blogs');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, [selectedCategory, searchQuery]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        per_page: 20
      };

      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      console.log('🔍 Fetching blogs from API with params:', params);
      const response = await axios.get(`${API_BASE_URL}/api/blogs`, { params });
      console.log('✅ API Response:', response.data);

      // Transform API data to match component structure
      const transformedBlogs = response.data.data.map(blog => ({
        id: blog.id,
        title: blog.title,
        excerpt: blog.excerpt,
        category: blog.category,
        author: blog.author_name || 'ALICE Team',
        date: blog.published_at || blog.created_at,
        readTime: `${blog.read_time_minutes} min read`,
        featured: blog.is_featured,
        tags: blog.tags
      }));

      console.log(`📚 Transformed ${transformedBlogs.length} blogs for display`);
      setBlogPosts(transformedBlogs);
    } catch (err) {
      console.error('❌ Error fetching blogs:', err);
      console.error('Error details:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories`);
      const apiCategories = response.data.data;

      // Transform categories
      const transformedCategories = [
        { key: 'all', label: 'All Topics', icon: '📚' },
        ...apiCategories.map(cat => ({
          key: cat.name,
          label: cat.name,
          icon: cat.icon
        }))
      ];

      setCategories(transformedCategories);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Keep mock blog data as fallback
  const mockBlogPosts = [
    {
      id: 1,
      title: 'The Future of AI in Talent Acquisition: 2025 Trends',
      excerpt: 'Discover how artificial intelligence is revolutionizing recruitment processes and what HR professionals need to know.',
      category: 'AI & Technology',
      author: 'Sarah Chen',
      date: '2025-01-15',
      readTime: '8 min read',
      featured: true,
      tags: ['AI', 'Recruitment', 'Technology', 'Future Trends']
    },
    {
      id: 2,
      title: 'Building Inclusive Hiring Practices: A Complete Guide',
      excerpt: 'Learn how to create more diverse and inclusive recruitment processes that attract top talent from all backgrounds.',
      category: 'Diversity & Inclusion',
      author: 'Marcus Johnson',
      date: '2025-01-12',
      readTime: '12 min read',
      featured: true,
      tags: ['Diversity', 'Inclusion', 'Hiring', 'Best Practices']
    },
    {
      id: 3,
      title: 'Remote Work Recruitment: Finding the Right Fit',
      excerpt: 'Strategies for identifying and hiring remote workers who will thrive in your organization.',
      category: 'Remote Work',
      author: 'Emily Rodriguez',
      date: '2025-01-10',
      readTime: '6 min read',
      featured: false,
      tags: ['Remote Work', 'Hiring', 'Culture Fit', 'Virtual Teams']
    },
    {
      id: 4,
      title: 'Candidate Experience Optimization: From Application to Offer',
      excerpt: 'Improve your candidate journey with these proven strategies for better engagement and conversion rates.',
      category: 'Candidate Experience',
      author: 'David Kim',
      date: '2025-01-08',
      readTime: '10 min read',
      featured: false,
      tags: ['Candidate Experience', 'Process Optimization', 'Engagement']
    },
    {
      id: 5,
      title: 'Data-Driven Recruitment: Metrics That Matter',
      excerpt: 'Key performance indicators every talent acquisition team should track for better hiring outcomes.',
      category: 'Analytics & Metrics',
      author: 'Lisa Wang',
      date: '2025-01-05',
      readTime: '7 min read',
      featured: false,
      tags: ['Analytics', 'KPIs', 'Data', 'Performance']
    },
    {
      id: 6,
      title: 'Employer Branding in the Digital Age',
      excerpt: 'How to build and maintain a strong employer brand that attracts top talent in competitive markets.',
      category: 'Employer Branding',
      author: 'Alex Thompson',
      date: '2025-01-03',
      readTime: '9 min read',
      featured: false,
      tags: ['Employer Branding', 'Digital Marketing', 'Talent Attraction']
    }
  ];

  const resources = [
    {
      id: 1,
      title: 'HR Technology Stack Guide 2025',
      type: 'Guide',
      description: 'Comprehensive guide to the latest HR technologies and tools for modern talent acquisition teams.',
      downloadUrl: '#',
      category: 'Technology'
    },
    {
      id: 2,
      title: 'Interview Question Bank',
      type: 'Template',
      description: 'Curated collection of behavioral and technical interview questions for various roles.',
      downloadUrl: '#',
      category: 'Interviewing'
    },
    {
      id: 3,
      title: 'Diversity & Inclusion Checklist',
      type: 'Checklist',
      description: 'Step-by-step checklist to ensure your hiring process promotes diversity and inclusion.',
      downloadUrl: '#',
      category: 'Diversity'
    },
    {
      id: 4,
      title: 'Candidate Assessment Rubric',
      type: 'Template',
      description: 'Standardized rubric for evaluating candidates across different competencies and skills.',
      downloadUrl: '#',
      category: 'Assessment'
    }
  ];

  // Use API data if available, otherwise show mock data
  const blogsToDisplay = blogPosts.length > 0 ? blogPosts : mockBlogPosts;

  const filteredBlogs = blogsToDisplay.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         blog.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleNavigation = (path) => {
    if (path.startsWith('http')) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="bg-white border-b border-[#e5e7eb] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src={logo}
                alt="CI Agent Logo"
                className="h-8 w-auto cursor-pointer hover:opacity-80 transition-opacity"
                onClick={handleLogoClick}
              />
            </div>
            <nav className="flex items-center text-sm font-medium text-[#4f4f4f] gap-6">
              <a href="#use-cases" className="hover:text-[#f7911d] transition">Use Cases</a>
              <a href="/community" className="hover:text-[#f7911d] transition text-[#f7911d]">Community</a>
              <a href="#candidates" className="hover:text-[#f7911d] transition">Candidates</a>
              <button onClick={() => handleNavigation('/login')} className="hover:text-[#f7911d] transition">Sign in</button>
              <button onClick={() => handleNavigation('/signup')} className="hover:text-[#f7911d] transition">Sign up</button>
              <button onClick={() => window.open('https://calendly.com/', '_blank')} className="ml-2 px-5 py-2 rounded-3xl bg-[#f7911d] text-white font-semibold shadow-md hover:bg-[#e67e0a] transition">Book Demo</button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0a0908] mb-4">
            HR & Talent Acquisition Community
          </h1>
          <p className="text-xl text-[#4f4f4f] mb-8 max-w-3xl mx-auto">
            Connect with fellow HR professionals, access expert insights, and discover the latest trends in talent acquisition.
          </p>

          {/* Tab Navigation */}
          <div className="flex justify-center space-x-1 bg-gray-100 rounded-lg p-1 max-w-md mx-auto">
            <button
              onClick={() => setActiveTab('blogs')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'blogs'
                  ? 'bg-white text-[#f7911d] shadow-sm'
                  : 'text-[#4f4f4f] hover:text-[#0a0908]'
              }`}
            >
              📝 Blog & Insights
            </button>
            <button
              onClick={() => setActiveTab('resources')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'resources'
                  ? 'bg-white text-[#f7911d] shadow-sm'
                  : 'text-[#4f4f4f] hover:text-[#0a0908]'
              }`}
            >
              📚 Resources & Tools
            </button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {activeTab === 'blogs' ? (
            <div>
              {/* Search and Filter */}
              <div className="mb-8">
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#9c9c9c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search articles, topics, or tags..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f7911d] focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Category Filter */}
                <div className="flex flex-wrap gap-2">
                  {(categories.length > 0 ? categories : [
                    { key: 'all', label: 'All Topics', icon: '📚' },
                    { key: 'AI & Technology', label: 'AI & Technology', icon: '🤖' },
                    { key: 'Diversity & Inclusion', label: 'Diversity & Inclusion', icon: '🌈' }
                  ]).map((category) => (
                    <button
                      key={category.key}
                      onClick={() => setSelectedCategory(category.key)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        selectedCategory === category.key
                          ? 'bg-[#f7911d] text-white'
                          : 'bg-white text-[#4f4f4f] border border-[#e5e7eb] hover:bg-[#f0f0f0]'
                      }`}
                    >
                      <span>{category.icon}</span>
                      <span>{category.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f7911d] mx-auto mb-4"></div>
                  <p className="text-[#4f4f4f]">Loading blog posts...</p>
                </div>
              )}

              {/* Featured Articles */}
              {!loading && (
                <>
                  <div className="mb-12">
                    <h2 className="text-2xl font-bold text-[#0a0908] mb-6">Featured Articles</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {filteredBlogs.filter(blog => blog.featured).map((blog) => (
                        <article key={blog.id} className="bg-white rounded-lg border border-[#e5e7eb] p-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-center space-x-2 text-sm text-[#4f4f4f] mb-3">
                            <span className="bg-[#f7911d] text-white px-2 py-1 rounded-full text-xs font-medium">Featured</span>
                            <span>{blog.category}</span>
                            <span>•</span>
                            <span>{blog.readTime}</span>
                          </div>
                          <h3
                            className="text-xl font-bold text-[#0a0908] mb-3 hover:text-[#f7911d] transition-colors cursor-pointer"
                            onClick={() => navigate(`/blog/${blog.id}`)}
                          >
                            {blog.title}
                          </h3>
                          <p className="text-[#4f4f4f] mb-4 line-clamp-3">{blog.excerpt}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 bg-[#f7911d] rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {blog.author.split(' ').map(n => n[0]).join('')}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-[#0a0908]">{blog.author}</p>
                                <p className="text-xs text-[#4f4f4f]">{new Date(blog.date).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => navigate(`/blog/${blog.id}`)}
                              className="text-[#f7911d] hover:text-[#e67e0a] font-medium"
                            >
                              Read More →
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>

                  {/* All Articles */}
                  <div>
                <h2 className="text-2xl font-bold text-[#0a0908] mb-6">All Articles</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredBlogs.map((blog) => (
                    <article key={blog.id} className="bg-white rounded-lg border border-[#e5e7eb] p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-center space-x-2 text-sm text-[#4f4f4f] mb-3">
                        <span>{blog.category}</span>
                        <span>•</span>
                        <span>{blog.readTime}</span>
                      </div>
                      <h3
                        className="text-lg font-bold text-[#0a0908] mb-3 hover:text-[#f7911d] transition-colors cursor-pointer"
                        onClick={() => navigate(`/blog/${blog.id}`)}
                      >
                        {blog.title}
                      </h3>
                      <p className="text-[#4f4f4f] mb-4 text-sm line-clamp-2">{blog.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-xs font-medium">
                            {blog.author.split(' ').map(n => n[0]).join('')}
                          </div>
                          <span className="text-sm text-[#4f4f4f]">{blog.author}</span>
                        </div>
                        <button
                          onClick={() => navigate(`/blog/${blog.id}`)}
                          className="text-[#f7911d] hover:text-[#e67e0a] text-sm font-medium"
                        >
                          Read →
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div>
              {/* Resources Section */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-[#0a0908] mb-6">Resources & Tools</h2>
                <p className="text-[#4f4f4f] mb-8">Download free templates, guides, and tools to improve your talent acquisition process.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {resources.map((resource) => (
                  <div key={resource.id} className="bg-white rounded-lg border border-[#e5e7eb] p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-[#f7911d] rounded-lg flex items-center justify-center text-white text-xl">
                        {resource.type === 'Guide' ? '📖' : resource.type === 'Template' ? '📋' : '✅'}
                      </div>
                      <span className="text-xs text-[#4f4f4f] bg-gray-100 px-2 py-1 rounded-full">
                        {resource.type}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[#0a0908] mb-3">{resource.title}</h3>
                    <p className="text-[#4f4f4f] mb-4 text-sm">{resource.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#4f4f4f] bg-gray-100 px-2 py-1 rounded-full">
                        {resource.category}
                      </span>
                      <button className="bg-[#f7911d] text-white px-4 py-2 rounded-lg hover:bg-[#e67e0a] transition-colors text-sm font-medium">
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Newsletter Signup */}
              <div className="mt-12 bg-[#fff0de] rounded-lg p-8 text-center">
                <h3 className="text-2xl font-bold text-[#0a0908] mb-4">Stay Updated</h3>
                <p className="text-[#4f4f4f] mb-6">Get the latest HR insights, trends, and resources delivered to your inbox.</p>
                <div className="flex flex-col md:flex-row gap-4 max-w-md mx-auto">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-3 border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f7911d] focus:border-transparent"
                  />
                  <button className="bg-[#f7911d] text-white px-6 py-3 rounded-lg hover:bg-[#e67e0a] transition-colors font-medium">
                    Subscribe
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-10 mt-10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8 px-6">
          <div>
            <img src={logo} alt="CI Agent Logo" className="h-8 mb-2" />
            <div className="text-gray-500 text-sm">© 2025 CI Agent</div>
            <div className="text-gray-400 text-xs mt-1">123 Main St, Singapore</div>
          </div>
          <div className="flex flex-col md:flex-row gap-8 flex-1 justify-between">
            <div>
              <div className="font-semibold text-gray-700 mb-2">Company</div>
              <ul className="text-gray-500 text-sm space-y-1">
                <li><button className="hover:text-orange-400 transition text-left">About us</button></li>
                <li><button className="hover:text-orange-400 transition text-left">Careers</button></li>
                <li><button className="hover:text-orange-400 transition text-left">Terms of service</button></li>
                <li><button className="hover:text-orange-400 transition text-left">Privacy policy</button></li>
                <li><button className="hover:text-orange-400 transition text-left">Contact us</button></li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-gray-700 mb-2">Resources</div>
              <ul className="text-gray-500 text-sm space-y-1">
                <li><button className="hover:text-orange-400 transition text-left">Windows App</button></li>
                <li><button className="hover:text-orange-400 transition text-left">Playbook</button></li>
                <li><button className="hover:text-orange-400 transition text-left">Blog</button></li>
                <li><button className="hover:text-orange-400 transition text-left">Help center</button></li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-gray-700 mb-2">Community</div>
              <ul className="text-gray-500 text-sm space-y-1">
                <li><button className="hover:text-orange-400 transition text-left">Events</button></li>
                <li><button className="hover:text-orange-400 transition text-left">Campus</button></li>
                <li><button className="hover:text-orange-400 transition text-left">Fellows</button></li>
              </ul>
            </div>
          </div>
          <div className="flex gap-4 mt-6 md:mt-0">
            <button className="text-gray-400 hover:text-orange-400"><i className="fab fa-linkedin fa-lg"></i></button>
            <button className="text-gray-400 hover:text-orange-400"><i className="fab fa-x-twitter fa-lg"></i></button>
            <button className="text-gray-400 hover:text-orange-400"><i className="fab fa-youtube fa-lg"></i></button>
            <button className="text-gray-400 hover:text-orange-400"><i className="fab fa-instagram fa-lg"></i></button>
            <button className="text-gray-400 hover:text-orange-400"><i className="fab fa-tiktok fa-lg"></i></button>
          </div>
        </div>
        <div className="text-center text-xs text-gray-400 mt-8">Less structure, more intelligence.</div>
      </footer>
    </div>
  );
};

export default Community;
